import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import { isAuthenticated, isGuest } from "./auth";
import ErrorBoundary from "./helpers/ErrorBoundary";

const Home = lazy(() => import("./containers/Home"));
const SearchContainer = lazy(() =>
  import("./containers/SearchContainer")
);
const CategoryContainer = lazy(() =>
  import("./containers/CategoryContainer")
);
const ProductDetailsContainer = lazy(() =>
  import("./containers/ProductDetailsContainer")
);
const MyAccountContainer = lazy(() =>
  import("./containers/MyAccountContainer")
);
const PageNotFoundContainer = lazy(() =>
  import("./containers/PageNotFoundContainer")
);
const OrdersContainer = lazy(() =>
  import("./containers/OrdersContainer")
);
const WishlistContainer = lazy(() =>
  import("./containers/WishlistContainer")
);
const OrdersSummeryContainer = lazy(() =>
  import("./containers/OrdersSummeryContainer")
);
const CheckoutContainer = lazy(() =>
  import("./containers/CheckoutContainer")
);
const BrandProductsContainer = lazy(() =>
  import("./containers/BrandProductsContainer")
);
const OrderPlacedContainer = lazy(() =>
  import("./containers/OrderPlacedContainer")
);
const FaqContainer = lazy(() => import("./containers/FaqContainer"));
const PrivacyPolicyContainer = lazy(() =>
  import("./containers/PrivacyPolicyContainer")
);
const ReturnPolicyContainer = lazy(() =>
  import("./containers/ReturnPolicyContainer")
);

const MobileAppPrivacyPolicyContainer = lazy(() =>
  import("./containers/MobileAppPrivacyPolicyContainer")
);
const TermConditionsContainer = lazy(() =>
  import("./containers/TermConditionsContainer")
);
const CancellationPolicyContainer = lazy(() =>
  import("./containers/CancellationPolicyContainer")
);
const CompanyContainer = lazy(() =>
  import("./containers/CompanyContainer")
);
const OrderReviewContainer = lazy(() =>
  import("./containers/OrderReviewContainer")
);
const ReturnContainer = lazy(() =>
  import("./containers/ReturnContainer")
);
const ReturnItemsContainer = lazy(() =>
  import("./containers/ReturnItemsContainer")
);
const OfferListContainer = lazy(() =>
  import("./containers/OfferListContainer")
);
const CampaignOfferContainer = lazy(() =>
  import("./containers/CampaignOfferContainer")
);
const SuperSaverContainer = lazy(() =>
  import("./containers/SuperSaverContainer")
);
const AddressContainer = lazy(() =>
  import("./containers/AddressContainer")
);
const AddAddressContainer = lazy(() =>
  import("./containers/AddAddressContainer")
);
const EditAddressContainer = lazy(() =>
  import("./containers/EditAddressContainer")
);
const ContactUsContainer = lazy(() =>
  import("./containers/ContactUsContainer")
);
const LotteryContainer = lazy(() =>
  import("./containers/LotteryContainer")
);
const DrawScreen = lazy(() => import("./containers/DrawScreen"));

const SweetDealsContainer = lazy(() =>
  import("./containers/SweetDealsContainer")
);

const DealsAndOffersContainer = lazy(() =>
  import("./containers/DealsAndOffersContainer")
);


const Login = lazy(() => import("./containers/Login"));
const Logout = lazy(() => import("./containers/Logout"));
const ForgotPassword = lazy(() => import("./containers/ForgotPassword"));
const Register = lazy(() => import("./containers/Register"));
const VerifyOTP = lazy(() => import("./containers/VerifyOTP"));
const ResetPasswordVerifyOTP = lazy(() =>
  import("./containers/ResetPasswordVerifyOTP")
);
const SiteLayout = lazy(() => import("./layouts/SiteLayout"));
const CheckoutLayout = lazy(() => import("./layouts/CheckoutLayout"));
const PlainLayout = lazy(() => import("./layouts/PlainLayout"));

/**
 * Private Route wrapper
 */
const PrivateRoute = ({ children, allowGuest = false }) => {
  const location = useLocation();

  if (isAuthenticated() && (allowGuest || !isGuest())) {
    return children;
  }

  const callBackUrl = `${location.pathname}${location.search}`;
  return <Navigate to="/login" replace state={{ callBackUrl }} />;
};


const routeList = [ 
  {
    path: "/",
     component: Home,
    layout: SiteLayout,
    exact: true,
    private: false,
  },
  {
    path: "/search",
     component: SearchContainer,
    layout: SiteLayout,
    exact: true,
    private: false,
  },
  {
    path: "/category/:can_name",
     component: CategoryContainer,
    layout: SiteLayout,
    exact: true,
    private: false,
  },
  
  {
    path: "/product/:sku",
     component: ProductDetailsContainer,
    layout: SiteLayout,
    exact: true,
    private: false,
  },
  {
    path: "/product/:sku/:can_name",
     component: ProductDetailsContainer,
    layout: SiteLayout,
    exact: true,
    private: false,
  },
  {
    path: "/login",
     component: Login,
    layout: PlainLayout,
    private: false,
  },
  {
    path: "/register",
     component: Register,
    layout: PlainLayout,
    private: false,
  },
  {
    path: "/logout",
     component: Logout,
    layout: PlainLayout,
    private: false,
  },
  {
    path: "/otp-verification/:token",
     component: VerifyOTP,
    layout: PlainLayout,
    private: false,
  },
  {
    path: "/checkout",
     component: CheckoutContainer,
    layout: CheckoutLayout,
    private: true,
    allowGuest: true,
  },
  {
    path: "/brand/:id",
     component: BrandProductsContainer,
    layout: SiteLayout,
    private: false,
  },
  {
    path: "/order-placed",
     component: OrderPlacedContainer,
    layout: SiteLayout,
    private: false,
  },

  {
    path: "/profile",
     component: MyAccountContainer,
    layout: SiteLayout,
    private: true,
  },
  {
    path: "/orders",
     component: OrdersContainer,
    layout: SiteLayout,
    private: true,
  },
  {
    path: "/order-summery/:id",
     component: OrdersSummeryContainer,
    layout: SiteLayout,
    private: true,
  },
  {
    path: "/reviews/:id",
     component: OrderReviewContainer,
    layout: SiteLayout,
    private: true,
  },
  {
    path: "/wishlists",
     component: WishlistContainer,
    layout: SiteLayout,
    private: true,
  },
  {
    path: "/returns",
     component: ReturnContainer,
    layout: SiteLayout,
    private: true,
  },
  {
    path: "/return-items/:id",
     component: ReturnItemsContainer,
    layout: SiteLayout,
    private: true,
  },
  {
    path: "/faq",
     component: FaqContainer,
    layout: SiteLayout,
    private: false,
  },
  {
    path: "/privacy-policy",
     component: PrivacyPolicyContainer,
    layout: SiteLayout,
    private: false,
  },
    {
    path: "/app-privacy-policy",
     component: MobileAppPrivacyPolicyContainer,
    layout: SiteLayout,
    private: false,
  },
  
  {
    path: "/return-policy",
     component: ReturnPolicyContainer,
    layout: SiteLayout,
    private: false,
  },
  {
    path: "/terms-conditions",
     component: TermConditionsContainer,
    layout: SiteLayout,
    private: false,
  },
  {
    path: "/cancellation-policy",
     component: CancellationPolicyContainer,
    layout: SiteLayout,
    private: false,
  },
  {
    path: "/company",
     component: CompanyContainer,
    layout: SiteLayout,
    private: false,
  },
  {
    path: "/forgot-password",
     component: ForgotPassword,
    layout: PlainLayout,
    private: false,
  },
  {
    path: "/address-book",
     component: AddressContainer,
    layout: SiteLayout,
    private: true,
  },
  {
    path: "/add-Address",
     component: AddAddressContainer,
    layout: SiteLayout,
    private: true,
  },
  {
    path: "/edit-address/:id",
     component: EditAddressContainer,
    layout: SiteLayout,
    private: true,
  },
  {
    path: "/reset-password/:token",
     component: ResetPasswordVerifyOTP,
    layout: PlainLayout,
    private: false,
  },
  {
    path: "/offer-listing/:itemId/:title",
     component: OfferListContainer,
    layout: SiteLayout,
    private: false,
  },
  {
    path: "/campaign/:itemId/:title",
     component: CampaignOfferContainer,
    layout: SiteLayout,
    private: false,
  },
  {
    path: "/super-saver/:itemId",
     component: SuperSaverContainer,
    layout: SiteLayout,
    private: false,
  },
  {
    path: "/contact-us",
     component: ContactUsContainer,
    layout: SiteLayout,
    private: false,
  },
  {
    path: "/draw/:code",
    component: LotteryContainer,
    layout: PlainLayout,
    private: false,
  },
  {
    path: "/draw-live/:code",
    component: DrawScreen,
    layout: PlainLayout,
    private: false,
  },
  {
    path: "",
     component: PageNotFoundContainer,
    layout: SiteLayout,
    private: false,
  },

  //--------- 2026 update
   {
    path: "/sweet-deals",
     component: SweetDealsContainer,
    layout: SiteLayout,
    private: false,
  },
   {
    path: "/deals-of-the-week",
     component: DealsAndOffersContainer,
    layout: SiteLayout,
    private: false,
  },

  
];

/**
 * Loader
 */
const Loader = () => (
  <div
    style={{
      height: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <span className="spinner-border" />
  </div>
);

/**
 * RoutesPage
 */
export const RoutesPage = () => {
  return (
    <Router>
      <Suspense fallback={<Loader />}>
        <Routes>
          {routeList.map(({ path, component: Component, layout: Layout, private: isPrivate, allowGuest }) => {
            const element = (
              <Layout>
                <Component />
              </Layout>
            );

            return (
              <Route
                key={path}
                path={path}
                element={
                  isPrivate ? (
                    <PrivateRoute allowGuest={allowGuest}>{element}</PrivateRoute>
                  ) : (
                    element
                  )
                }
              />
            );
          })}
        </Routes>
      </Suspense>
    </Router>
  );
};